import React, { useCallback, useContext, useMemo, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';

import ReportContext from 'contexts/ReportContext';
import PopupReportUsageLookupProvider, {
  PopupReportUsageLookupContext,
} from 'contextProviders/SplashScreenProviders/ContentDependantProviders/PopupReportUsageLookupProvider';
import Button from 'kingpin/atoms/Button';
import { Option } from 'kingpin/atoms/ContextMenu';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import AccessInput from 'components/AccessInput';
import Checkbox from 'components/Inputs/Checkbox';
import Row from 'components/Common/Row';
import Inputs from 'components/Inputs';
import usePopup from 'hooks/usePopup';
import Alert from '../../kingpin/Alert';
import useConvertReportToDashboard from './useConvertReportToDashboard';

// Inside the popup usage lookup, which is only built once the modal opens
const ConvertReportToDashboardForm = ({ close }: { close: () => void }) => {
  const { report } = useContext(ReportContext);
  const { popupReportsLookup } = useContext(PopupReportUsageLookupContext);
  const { convertReportToDashboard, isLoading } = useConvertReportToDashboard();

  const [dashboardName, setDashboardName] = useState('');
  const [access, setAccess] = useState<ResourceAccess>({
    version: '2',
    type: 'Private',
  });
  const [keepOriginal, setKeepOriginal] = useState(false);

  const isPopupReport = useMemo(() => {
    if (!report) return false;
    const usage = popupReportsLookup[report.id];
    return usage && usage.length > 0;
  }, [report, popupReportsLookup]);

  const onDashboardNameChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDashboardName(event.target.value);
    },
    [],
  );

  const handleConvert = useCallback(async () => {
    if (!report) {
      return;
    }

    await convertReportToDashboard({
      report,
      dashboardName,
      access,
      keepOriginal,
      onSuccess: close,
    });
  }, [
    report,
    dashboardName,
    access,
    keepOriginal,
    convertReportToDashboard,
    close,
  ]);

  return (
    <Form data-testid="convert-report-to-dashboard-form">
      <FormHeader title={'Convert to Dashboard'} onClose={close} />
      <FormContent>
        <div style={{ marginBottom: 16 }}>
          <Typography.Body type="Label">
            Dashboard Name (max. 40 characters)
          </Typography.Body>
          <Inputs.TextInput
            value={dashboardName}
            onChange={onDashboardNameChanged}
            data-testid={'dashboard-name'}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <AccessInput
            access={access}
            setAccess={setAccess}
            canSetToPrivate={true}
          />
        </div>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Checkbox
            isChecked={keepOriginal}
            onToggled={setKeepOriginal}
            data-testid="keep-original-checkbox"
          />
          <Typography.Body type="Body 12">
            Keep original report after conversion
          </Typography.Body>
        </div>
        {isPopupReport && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              backgroundColor: 'var(--color-warning-bg)',
              borderRadius: 4,
              border: '1px solid var(--color-warning-border)',
            }}
          >
            <Alert
              type={'Danger'}
              body={`This report is used as a popup report in
                dashboards, scorecards, or goals. If you delete the
                original report, those references will be broken`}
            ></Alert>
          </div>
        )}
      </FormContent>
      <Row centerAlign spaceBetween style={{ marginBottom: 16 }}>
        <Button
          onClick={close}
          isDisabled={isLoading}
          type="Ghost"
          size="Small"
          label="Cancel"
        />
        <Button
          type="Primary"
          size="Small"
          label="Convert"
          onClick={handleConvert}
          isLoading={isLoading}
          isDisabled={
            dashboardName === '' ||
            isLoading ||
            (isPopupReport && !keepOriginal)
          }
          data-testid="convert-button"
        />
      </Row>
    </Form>
  );
};

const ConvertReportToDashboardButton = () => {
  const { isOpen, open, close } = usePopup(false, undefined, true);

  return (
    <React.Fragment>
      <Option
        label={'Convert to Dashboard'}
        onClick={open}
        shouldHideInsteadOfCloseMenu
      />
      <ModalTransition>
        {isOpen && (
          <Modal
            onClose={close}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            <PopupReportUsageLookupProvider>
              <ConvertReportToDashboardForm close={close} />
            </PopupReportUsageLookupProvider>
          </Modal>
        )}
      </ModalTransition>
    </React.Fragment>
  );
};

export default ConvertReportToDashboardButton;
